import { useLazyQuery }             from '@apollo/client'
import { useEffect, useState }      from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams }   from 'react-router-dom'
import { LoadingEllipsis }          from '../../../components/Loaders'
import SettingsShopProductForm      from '../../../components/Settings/ShopSettings/SettingsShopProductForm'

import { setShopCategories, setShopDeliveryOptions, setShopSizeGuides, setShopVariants, setShopVariantsVariations } from '../../../features/shop/shopSlice'
import {
  setToasts,
  showToaster
}                                                                                                                   from '../../../features/toaster/toasterSlice'
import {
  GET_SHOP_CATEGORIES,
  GET_SHOP_DELIVERY_OPTIONS,
  GET_SHOP_PRODUCT_BY_ID,
  GET_SHOP_SIZE_GUIDES,
  GET_SHOP_VARIANTS,
  GET_SHOP_VARIANTS_VARIATIONS
}                                                                                                                   from '../../../graphql/shop'
import MainLayout                                                                                                   from '../../../layouts/MainLayout'

export default function SettingsShopProduct(): JSX.Element {
  const { product_id }                  = useParams()
  const dispatch: any                   = useDispatch()
  const navigate: any                   = useNavigate()
  const categories: any                 = useSelector( ( state: any ): any => state.shop.categories )
  const deliveryOptions: any            = useSelector( ( state: any ): any => state.shop.deliveryOptions )
  const sizeGuides: any                 = useSelector( ( state: any ): any => state.shop.sizeGuides )
  const currentTenant: any              = useSelector( ( state: any ): any => state.currentTenant.tenant )
  const currentUser: any                = useSelector( ( state: any ): any => state.currentUser.user )
  const shopVariants: any               = useSelector( ( state: any ): any => state.shop.variants )
  const shopVariantsVariations: any     = useSelector( ( state: any ): any => state.shop.variantsVariations )
  const [ shopProduct, setShopProduct ] = useState( null )

  const [ getShopProduct, { loading, refetch: refetchProduct } ] = useLazyQuery( GET_SHOP_PRODUCT_BY_ID, {
    fetchPolicy:     'network-only',
    nextFetchPolicy: 'network-only',
    onCompleted:     ( { shopProductById }: any ): void => {
      if ( shopProductById ) {
        setShopProduct( shopProductById )
      } else {
        dispatchMessage( 'error', 'Product not found.' )
        navigate( '/settings/shop/products' )
      }
    }
  } )

  const [ getCategories, { loading: loadingCategories } ] = useLazyQuery( GET_SHOP_CATEGORIES, {
    onCompleted: ( { shopCategories }: any ): void => dispatch( setShopCategories( shopCategories ) )
  } )

  const [ getDeliveryOptions, { loading: loadingDeliveryOptions } ] = useLazyQuery( GET_SHOP_DELIVERY_OPTIONS, {
    onCompleted: ( { shopDeliveryOptions }: any ): void => dispatch( setShopDeliveryOptions( shopDeliveryOptions ) )
  } )

  const [ getSizeGuides, { loading: loadingSizeGuides } ] = useLazyQuery( GET_SHOP_SIZE_GUIDES, {
    onCompleted: ( { shopSizeGuides }: any ): void => dispatch( setShopSizeGuides( shopSizeGuides ) )
  } )

  const [ getShopVariants, { loading: loadingVariants } ] = useLazyQuery( GET_SHOP_VARIANTS, {
    onCompleted: ( { shopVariants }: any ): void => dispatch( setShopVariants( shopVariants ) )
  } )

  const [ getShopVariantsVariations, { loading: loadingVariantsVariations } ] = useLazyQuery( GET_SHOP_VARIANTS_VARIATIONS, {
    onCompleted: ( { shopVariantsVariations }: any ): void => dispatch( setShopVariantsVariations( shopVariantsVariations ) )
  } )

  const fetchShopVariants: any = async (): Promise<void> => {
    await getShopVariants()
  }

  const fetchShopVariantsVariations: any = async (): Promise<void> => {
    await getShopVariantsVariations()
  }

  const fetchShopProduct: any = async (): Promise<void> => {
    await getShopProduct( { variables: { input: { id: product_id } } } )
  }

  const fetchCategories: any = async (): Promise<void> => {
    await getCategories()
  }

  const fetchDeliveryOptions: any = async (): Promise<void> => {
    await getDeliveryOptions()
  }

  const fetchSizeGuides: any = async (): Promise<void> => {
    await getSizeGuides()
  }

  const dispatchMessage: any = ( type: string, message: string ): void => {
    dispatch( setToasts( [ { id: '1', type, message } ] ) )
    dispatch( showToaster( true ) )
  }

  useEffect( (): any => {
    let shouldFetch: boolean = true

    if ( shouldFetch ) {
      if ( !loading && product_id ) {
        fetchShopProduct()
      }

      if ( !loadingCategories && !categories.length ) {
        fetchCategories()
      }

      if ( !loadingDeliveryOptions && !deliveryOptions.length ) {
        fetchDeliveryOptions()
      }

      if ( !loadingSizeGuides && !sizeGuides.length ) {
        fetchSizeGuides()
      }

      if ( !loadingVariants && !shopVariants.length ) {
        fetchShopVariants()
      }

      if ( !loadingVariantsVariations && !shopVariantsVariations.length ) {
        fetchShopVariantsVariations()
      }
    }

    return (): void => {
      shouldFetch = false
    }
  }, [ product_id, categories, deliveryOptions, sizeGuides, shopVariants, shopVariantsVariations ] )

  return (
    <MainLayout>
      <div className="max-w-screen-3xl mx-auto w-full">
        <div className="flex-grow lg:p-8 p-3 space-y-4 lg:space-y-8">
          { ( loading || !currentUser || loadingCategories || loadingDeliveryOptions || loadingSizeGuides ) &&
            <LoadingEllipsis klass="ellipse-yellow ellipse-xl p-4 mt-4 w-full" />
          }
          { !loading && !loadingCategories && !loadingDeliveryOptions && !loadingSizeGuides && currentUser?.admin &&
            <>
              { shopProduct &&
                <SettingsShopProductForm
                  shopProduct={ shopProduct }
                  categories={ categories }
                  deliveryOptions={ deliveryOptions }
                  sizeGuides={ sizeGuides }
                  currentTenant={ currentTenant }
                  refetchProduct={ refetchProduct }
                />
              }

              { !shopProduct &&
                <SettingsShopProductForm
                  categories={ categories }
                  deliveryOptions={ deliveryOptions }
                  sizeGuides={ sizeGuides }
                  currentTenant={ currentTenant }
                />
              }
            </>
          }
          { !currentUser?.admin &&
            <div className="row flex flex-col items-center w-full bg-white rounded-3xl p-3 xl:p-4">
              Access Denied!
            </div>
          }
        </div>
      </div>
    </MainLayout>
  )
}
